// src/components/common/taskbox/TeamTasksWidget.js
import React, { useState } from 'react';
import { Users, Calendar, Clock, CheckCircle, AlertCircle, Play, Pause, RefreshCw, MessageSquare, Paperclip, Activity, Eye, X, CheckSquare, Search, Grid, List, Calendar as CalendarIcon, BarChart3, User } from 'lucide-react';
import { useEmployees } from '../../../contexts/EmployeeContext';
import teamTasksService from '../../../services/teamTasks.service';
import taskService from '../../../services/task.service';

const TeamTasksWidget = ({ teamTasks, activities, stats, onRefresh, theme, user }) => {
    const { employees } = useEmployees();
    const [selectedTask, setSelectedTask] = useState(null);
    const [showDetailModal, setShowDetailModal] = useState(false);
    const [loading, setLoading] = useState(false);
    const [activeTab, setActiveTab] = useState('overview');
    const [newComment, setNewComment] = useState('');
    const [selectedFile, setSelectedFile] = useState(null);
    const [taskDetails, setTaskDetails] = useState(null);
    const [loadingDetails, setLoadingDetails] = useState(false);
    const [subtaskComments, setSubtaskComments] = useState({});
    const [attachmentDescription, setAttachmentDescription] = useState('');
    
    // Search and filter state
    const [searchTerm, setSearchTerm] = useState('');
    const [statusFilter, setStatusFilter] = useState('all');
    const [priorityFilter, setPriorityFilter] = useState('all');
    const [viewMode, setViewMode] = useState('card'); // card, list, kanban, calendar
    const [showAnalytics, setShowAnalytics] = useState(false);
    const [currentMonth, setCurrentMonth] = useState(new Date());

    const getStatusIcon = (status) => {
        const icons = {
            pending: <Clock className="w-4 h-4" />,
            in_progress: <Play className="w-4 h-4" />,
            under_review: <AlertCircle className="w-4 h-4" />,
            completed: <CheckCircle className="w-4 h-4" />,
            on_hold: <Pause className="w-4 h-4" />
        };
        return icons[status] || <Clock className="w-4 h-4" />;
    };

    const getStatusColor = (status) => {
        const colors = {
            pending: 'text-gray-600 bg-gray-100',
            in_progress: 'text-blue-600 bg-blue-100',
            under_review: 'text-yellow-600 bg-yellow-100',
            completed: 'text-green-600 bg-green-100',
            on_hold: 'text-orange-600 bg-orange-100'
        };
        return colors[status] || colors.pending;
    };

    const getPriorityColor = (priority) => {
        const colors = {
            immediate: 'text-red-600 bg-red-100',
            urgent: 'text-orange-600 bg-orange-100',
            next_in_line: 'text-yellow-600 bg-yellow-100',
            scheduled: 'text-green-600 bg-green-100',
            deferred: 'text-gray-600 bg-gray-100'
        };
        return colors[priority] || colors.scheduled;
    };

    const getEmployeeName = (employeeId) => {
        // Handle both string and integer ID comparisons
        const employee = employees.find(emp =>
            emp.id === employeeId ||
            emp.id.toString() === employeeId.toString() ||
            emp.id === parseInt(employeeId) ||
            emp.id === String(employeeId)
        );
        return employee ? `${employee.first_name} ${employee.last_name}` : 'Unknown';
    };

    const getAssignedEmployeeNames = (task) => {
        // If backend provides assigned_to_names, use it directly
        if (task.assigned_to_names && Array.isArray(task.assigned_to_names)) {
            return task.assigned_to_names.filter(name => name && name.trim() !== '');
        }

        // Fallback: if we have assigned_to_name (single), use it
        if (task.assigned_to_name && task.assigned_to_name.trim() !== '') {
            return [task.assigned_to_name];
        }

        // Final fallback: try to resolve from employee IDs
        if (task.assigned_employee_ids && Array.isArray(task.assigned_employee_ids)) {
            return task.assigned_employee_ids.map(empId => getEmployeeName(empId)).filter(name => name !== 'Unknown');
        }

        return [];
    };

  
    const handleStatusUpdate = async (taskId, newStatus) => {
        try {
            setLoading(true);
            await teamTasksService.updateTeamTask(taskId, { status: newStatus });
            onRefresh();
        } catch (error) {
            console.error('Error updating task status:', error);
            alert('Failed to update task status');
        } finally {
            setLoading(false);
        }
    };

  
    const canEditTask = (task) => {
        return task.created_by === user.id || ['admin', 'Admin'].includes(user.role);
    };

    // Permission functions for assignees
    const isAssignee = (task) => {
        if (!task || !user) return false;
        const assignedIds = Array.isArray(task.assigned_employee_ids) ? task.assigned_employee_ids : [];
        return assignedIds.includes(user.id);
    };

    const canInteractWithTask = (task) => {
        if (!task || !user) return false;
        return canEditTask(task) || isAssignee(task);
    };

    const canChangeSubtaskStatus = (task, subtask) => {
        if (!task || !user || !subtask) return false;

        // Admin/Manager can change any subtask
        if (canEditTask(task)) return true;

        // Assignees can change subtask status if they are assigned to that subtask
        if (isAssignee(task)) {
            const assignedIds = Array.isArray(subtask.assigned_employee_ids)
                ? subtask.assigned_employee_ids
                : (subtask.assigned_to_employee_id ? [subtask.assigned_to_employee_id] : []);
            return assignedIds.includes(user.id);
        }

        return false;
    };

    // Fetch detailed task information
    const fetchTaskDetails = async (taskId) => {
        try {
            setLoadingDetails(true);
            const response = await teamTasksService.getTeamTask(taskId);
            if (response.success) {
                setTaskDetails(response.data);
            }
        } catch (error) {
            console.error('Error fetching task details:', error);
        } finally {
            setLoadingDetails(false);
        }
    };

    // Handle task click with details fetch
    const handleTaskClick = async (task) => {
        setSelectedTask(task);
        setShowDetailModal(true);
        setActiveTab('overview');
        await fetchTaskDetails(task.id);
    };

    // Handle comment submission
    const handleAddComment = async () => {
        if (!newComment.trim() || !selectedTask) return;

        try {
            const response = await teamTasksService.addComment(selectedTask.id, {
                comment_text: newComment
            });
            if (response.success) {
                await fetchTaskDetails(selectedTask.id);
                setNewComment('');
                // Refresh the main task list to update comment counts for all watchers
                onRefresh();
            }
        } catch (error) {
            console.error('Error adding comment:', error);
            alert('Failed to add comment. Please try again.');
        }
    };

    // Handle subtask comment submission
    const handleAddSubtaskComment = async (subtaskId) => {
        const comment = subtaskComments[subtaskId];
        if (!comment || !comment.trim() || !selectedTask) return;

        try {
            const response = await teamTasksService.addComment(selectedTask.id, {
                comment_text: comment,
                subtask_id: subtaskId
            });
            if (response.success) {
                await fetchTaskDetails(selectedTask.id);
                setSubtaskComments({ ...subtaskComments, [subtaskId]: '' });
                // Refresh the main task list to update comment counts for all watchers
                onRefresh();
            }
        } catch (error) {
            console.error('Error adding subtask comment:', error);
            alert('Failed to add comment. Please try again.');
        }
    };

    // Handle subtask status update
    const handleUpdateSubtaskStatus = async (subtaskId, newStatus) => {
        if (!selectedTask) return;

        try {
            await teamTasksService.updateSubTask(selectedTask.id, subtaskId, { status: newStatus });
            await fetchTaskDetails(selectedTask.id);

            // After updating subtask, check if all subtasks are now completed
            setTimeout(async () => {
                try {
                    // Get fresh task details to check current subtask statuses
                    const response = await teamTasksService.getTeamTask(selectedTask.id);
                    if (response.success) {
                        const updatedTask = response.data;
                        const allSubtasks = updatedTask.subtasks || [];

                        // Check if all subtasks are completed
                        const allCompleted = allSubtasks.length > 0 &&
                            allSubtasks.every(st => st.status === 'completed');

                        // Check if all subtasks were completed and task status should change
                        if (allCompleted && updatedTask.status !== 'under_review' && updatedTask.status !== 'completed') {
                            // Auto-move task to under_review when all subtasks are completed
                            await teamTasksService.updateTeamTask(selectedTask.id, { status: 'under_review' });
                            await fetchTaskDetails(selectedTask.id);
                            // Refresh the main task list to update subtask counts for all watchers
                            onRefresh();
                        } else {
                            // Refresh the main task list to update subtask counts for all watchers
                            onRefresh();
                        }
                    } else {
                        // Refresh the main task list to update subtask counts for all watchers
                        onRefresh();
                    }
                } catch (error) {
                    console.error('Error checking subtask completion status:', error);
                    // Refresh the main task list to update subtask counts for all watchers
                    onRefresh(); // Fallback to regular refresh
                }
            }, 500); // Small delay to ensure backend has processed the update

        } catch (error) {
            console.error('Error updating subtask:', error);
        }
    };

    // Handle file upload
    const handleFileUpload = async () => {
        if (!selectedFile || !selectedTask) return;

        try {
            const uploadResponse = await teamTasksService.uploadFile(selectedFile);

            if (uploadResponse.success) {
                const response = await teamTasksService.addAttachment(selectedTask.id, {
                    file_name: selectedFile.name,
                    file_url: uploadResponse.data?.downloadURL || uploadResponse.data?.url,
                    file_type: selectedFile.type,
                    file_size: selectedFile.size,
                    file_path: uploadResponse.data?.filePath,
                    description: attachmentDescription || null
                });

                if (response.success) {
                    await fetchTaskDetails(selectedTask.id);
                    setSelectedFile(null);
                    setAttachmentDescription('');
                    // Refresh the main task list to update attachment counts for all watchers
                    onRefresh();
                }
            }
        } catch (error) {
            console.error('Error uploading file:', error);
            alert('Failed to upload file. Please try again.');
        }
    };

    // Filter tasks for the current user (assignee view)
    const getFilteredTasks = () => {
        let filteredTasks = teamTasks.filter(task => {
            // Only show tasks assigned to the current user OR created by the current user
            const isAssigned = task.assigned_employee_ids?.includes(user.id) ||
                               task.assigned_to === user.id ||
                               task.assigned_to_name?.includes(user.name) ||
                               task.assigned_to_names?.some(name => name.includes(user.name)) ||
                               task.created_by === user.id;

            if (!isAssigned) return false;

            // Apply search filter
            if (searchTerm) {
                const searchLower = searchTerm.toLowerCase();
                const matchesSearch = task.title.toLowerCase().includes(searchLower) ||
                                     (task.description && task.description.toLowerCase().includes(searchLower)) ||
                                     getAssignedEmployeeNames(task).some(name => name.toLowerCase().includes(searchLower));
                if (!matchesSearch) return false;
            }

            // Apply status filter
            if (statusFilter !== 'all' && task.status !== statusFilter) {
                return false;
            }

            // Apply priority filter
            if (priorityFilter !== 'all' && task.priority !== priorityFilter) {
                return false;
            }

            return true;
        });

        return filteredTasks;
    };

    // Calculate accurate stats for filtered tasks
    const calculateStats = () => {
        const filteredTasks = getFilteredTasks();
        return {
            total: filteredTasks.length,
            pending: filteredTasks.filter(task => task.status === 'pending').length,
            inProgress: filteredTasks.filter(task => task.status === 'in_progress').length,
            completed: filteredTasks.filter(task => task.status === 'completed').length,
            onHold: filteredTasks.filter(task => task.status === 'on_hold').length,
            underReview: filteredTasks.filter(task => task.status === 'under_review').length
        };
    };

    // Define tabs for the modal
    const tabs = [
        { id: 'overview', label: 'Overview', icon: Activity },
        { id: 'subtasks', label: 'Sub-tasks', icon: CheckSquare, count: taskDetails?.subtasks?.length },
        { id: 'comments', label: 'Comments', icon: MessageSquare, count: taskDetails?.comments?.length },
        { id: 'attachments', label: 'Attachments', icon: Paperclip, count: taskDetails?.attachments?.length },
        { id: 'activity', label: 'Activity', icon: Activity },
        { id: 'watchers', label: 'Watchers', icon: Eye, count: taskDetails?.watchers?.length }
    ];

    return (
        <div className={`rounded-xl p-6 border ${
            theme === 'dark' ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
        }`}>
            {/* Header with Search and Filters */}
            <div className="mb-6">
                <div className="flex items-center justify-between mb-4">
                    <div>
                        <h3 className={`text-xl font-semibold ${
                            theme === 'dark' ? 'text-white' : 'text-gray-900'
                        }`}>
                            Team Tasks
                        </h3>
                        <p className={`text-sm mt-1 ${
                            theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
                        }`}>
                            Tasks assigned to you or created by you
                        </p>
                    </div>
                    <div className="flex items-center gap-2">
                        <button
                            onClick={() => setShowAnalytics(!showAnalytics)}
                            className={`p-2 rounded-lg border ${
                                theme === 'dark'
                                    ? 'bg-gray-700 border-gray-600 hover:bg-gray-600'
                                    : 'bg-white border-gray-300 hover:bg-gray-50'
                            }`}
                            title="Toggle Analytics"
                        >
                            <BarChart3 className="w-5 h-5" />
                        </button>
                        <button
                            onClick={onRefresh}
                            disabled={loading}
                            className={`p-2 rounded-lg border ${
                                theme === 'dark'
                                    ? 'bg-gray-700 border-gray-600 hover:bg-gray-600'
                                    : 'bg-white border-gray-300 hover:bg-gray-50'
                            } ${loading ? 'opacity-50 cursor-not-allowed' : ''}`}
                            title="Refresh tasks"
                        >
                            <RefreshCw className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`} />
                        </button>
                    </div>
                </div>

                {/* Search and Filter Bar */}
                <div className="flex flex-col md:flex-row gap-3 mb-4">
                    {/* Search Bar */}
                    <div className="flex-1 relative">
                        <Search className={`absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 ${
                            theme === 'dark' ? 'text-gray-400' : 'text-gray-500'
                        }`} />
                        <input
                            type="text"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            placeholder="Search tasks..."
                            className={`w-full pl-10 pr-4 py-2 border rounded-lg ${
                                theme === 'dark'
                                    ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400'
                                    : 'bg-white border-gray-300 placeholder-gray-500'
                            }`}
                        />
                    </div>

                    {/* Status Filter */}
                    <select
                        value={statusFilter}
                        onChange={(e) => setStatusFilter(e.target.value)}
                        className={`px-4 py-2 border rounded-lg ${
                            theme === 'dark'
                                ? 'bg-gray-700 border-gray-600 text-white'
                                : 'bg-white border-gray-300'
                        }`}
                    >
                        <option value="all">All Status</option>
                        <option value="pending">Pending</option>
                        <option value="in_progress">In Progress</option>
                        <option value="under_review">Under Review</option>
                        <option value="completed">Completed</option>
                        <option value="on_hold">On Hold</option>
                    </select>

                    {/* Priority Filter */}
                    <select
                        value={priorityFilter}
                        onChange={(e) => setPriorityFilter(e.target.value)}
                        className={`px-4 py-2 border rounded-lg ${
                            theme === 'dark'
                                ? 'bg-gray-700 border-gray-600 text-white'
                                : 'bg-white border-gray-300'
                        }`}
                    >
                        <option value="all">All Priority</option>
                        <option value="immediate">Immediate</option>
                        <option value="urgent">Urgent</option>
                        <option value="next_in_line">Next in Line</option>
                        <option value="scheduled">Scheduled</option>
                        <option value="deferred">Deferred</option>
                    </select>

                    {/* View Mode Toggle */}
                    <div className={`flex border rounded-lg ${
                        theme === 'dark' ? 'border-gray-600' : 'border-gray-300'
                    }`}>
                        <button
                            onClick={() => setViewMode('card')}
                            className={`px-3 py-2 rounded-l-lg ${
                                viewMode === 'card'
                                    ? theme === 'dark' ? 'bg-gray-600 text-white' : 'bg-gray-100 text-gray-900'
                                    : theme === 'dark' ? 'bg-gray-700 text-gray-400 hover:bg-gray-600' : 'bg-white text-gray-600 hover:bg-gray-50'
                            }`}
                            title="Card View"
                        >
                            <Grid className="w-4 h-4" />
                        </button>
                        <button
                            onClick={() => setViewMode('list')}
                            className={`px-3 py-2 ${
                                viewMode === 'list'
                                    ? theme === 'dark' ? 'bg-gray-600 text-white' : 'bg-gray-100 text-gray-900'
                                    : theme === 'dark' ? 'bg-gray-700 text-gray-400 hover:bg-gray-600' : 'bg-white text-gray-600 hover:bg-gray-50'
                            }`}
                            title="List View"
                        >
                            <List className="w-4 h-4" />
                        </button>
                        <button
                            onClick={() => setViewMode('kanban')}
                            className={`px-3 py-2 rounded-r-lg ${
                                viewMode === 'kanban'
                                    ? theme === 'dark' ? 'bg-gray-600 text-white' : 'bg-gray-100 text-gray-900'
                                    : theme === 'dark' ? 'bg-gray-700 text-gray-400 hover:bg-gray-600' : 'bg-white text-gray-600 hover:bg-gray-50'
                            }`}
                            title="Kanban View"
                        >
                            <CalendarIcon className="w-4 h-4" />
                        </button>
                    </div>
                </div>
            </div>

            {/* Analytics Dashboard */}
            {showAnalytics && (
                <div className={`p-6 rounded-lg border mb-6 ${
                    theme === 'dark' ? 'bg-gray-700 border-gray-600' : 'bg-gray-50 border-gray-200'
                }`}>
                    <div className="flex items-center justify-between mb-4">
                        <h4 className={`font-semibold ${
                            theme === 'dark' ? 'text-white' : 'text-gray-900'
                        }`}>
                            Task Analytics
                        </h4>
                        <button
                            onClick={() => setShowAnalytics(false)}
                            className={`p-1 rounded hover:bg-gray-200 dark:hover:bg-gray-600`}
                        >
                            <X className="w-4 h-4" />
                        </button>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div className={`p-4 rounded-lg ${
                            theme === 'dark' ? 'bg-gray-800' : 'bg-white'
                        }`}>
                            <h5 className={`text-sm font-medium mb-2 ${
                                theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                            }`}>
                                Completion Rate
                            </h5>
                            <div className="text-2xl font-bold text-green-600">
                                {(() => {
                                    const accurateStats = calculateStats();
                                    const rate = accurateStats.total > 0 ? Math.round((accurateStats.completed / accurateStats.total) * 100) : 0;
                                    return `${rate}%`;
                                })()}
                            </div>
                            <div className="text-xs text-gray-500 mt-1">
                                {(() => {
                                    const accurateStats = calculateStats();
                                    return `${accurateStats.completed} of ${accurateStats.total} tasks`;
                                })()}
                            </div>
                        </div>
                        <div className={`p-4 rounded-lg ${
                            theme === 'dark' ? 'bg-gray-800' : 'bg-white'
                        }`}>
                            <h5 className={`text-sm font-medium mb-2 ${
                                theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                            }`}>
                                Active Tasks
                            </h5>
                            <div className="text-2xl font-bold text-blue-600">
                                {(() => {
                                    const accurateStats = calculateStats();
                                    return accurateStats.inProgress + accurateStats.underReview;
                                })()}
                            </div>
                            <div className="text-xs text-gray-500 mt-1">
                                Currently being worked on
                            </div>
                        </div>
                        <div className={`p-4 rounded-lg ${
                            theme === 'dark' ? 'bg-gray-800' : 'bg-white'
                        }`}>
                            <h5 className={`text-sm font-medium mb-2 ${
                                theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                            }`}>
                                Pending Tasks
                            </h5>
                            <div className="text-2xl font-bold text-orange-600">
                                {(() => {
                                    const accurateStats = calculateStats();
                                    return accurateStats.pending + accurateStats.onHold;
                                })()}
                            </div>
                            <div className="text-xs text-gray-500 mt-1">
                                Awaiting to start
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Stats */}
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 mb-6">
                <div className={`p-3 rounded-lg border ${
                    theme === 'dark' ? 'bg-gray-700 border-gray-600' : 'bg-gray-50 border-gray-200'
                }`}>
                    <div className="flex items-center gap-2 mb-2">
                        <Clock className="w-4 h-4 text-gray-600" />
                        <span className={`text-xs font-medium ${
                            theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
                        }`}>
                            Pending
                        </span>
                    </div>
                    <p className={`text-xl font-bold ${
                        theme === 'dark' ? 'text-white' : 'text-gray-900'
                    }`}>
                        {calculateStats().pending}
                    </p>
                </div>
                <div className={`p-3 rounded-lg border ${
                    theme === 'dark' ? 'bg-gray-700 border-gray-600' : 'bg-gray-50 border-gray-200'
                }`}>
                    <div className="flex items-center gap-2 mb-2">
                        <Play className="w-4 h-4 text-blue-600" />
                        <span className={`text-xs font-medium ${
                            theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
                        }`}>
                            In Progress
                        </span>
                    </div>
                    <p className="text-xl font-bold text-blue-600">
                        {calculateStats().inProgress}
                    </p>
                </div>
                <div className={`p-3 rounded-lg border ${
                    theme === 'dark' ? 'bg-gray-700 border-gray-600' : 'bg-gray-50 border-gray-200'
                }`}>
                    <div className="flex items-center gap-2 mb-2">
                        <AlertCircle className="w-4 h-4 text-yellow-600" />
                        <span className={`text-xs font-medium ${
                            theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
                        }`}>
                            Under Review
                        </span>
                    </div>
                    <p className="text-xl font-bold text-yellow-600">
                        {calculateStats().underReview}
                    </p>
                </div>
                <div className={`p-3 rounded-lg border ${
                    theme === 'dark' ? 'bg-gray-700 border-gray-600' : 'bg-gray-50 border-gray-200'
                }`}>
                    <div className="flex items-center gap-2 mb-2">
                        <CheckCircle className="w-4 h-4 text-green-600" />
                        <span className={`text-xs font-medium ${
                            theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
                        }`}>
                            Completed
                        </span>
                    </div>
                    <p className="text-xl font-bold text-green-600">
                        {calculateStats().completed}
                    </p>
                </div>
                <div className={`p-3 rounded-lg border ${
                    theme === 'dark' ? 'bg-gray-700 border-gray-600' : 'bg-gray-50 border-gray-200'
                }`}>
                    <div className="flex items-center gap-2 mb-2">
                        <Pause className="w-4 h-4 text-orange-600" />
                        <span className={`text-xs font-medium ${
                            theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
                        }`}>
                            On Hold
                        </span>
                    </div>
                    <p className="text-xl font-bold text-orange-600">
                        {calculateStats().onHold}
                    </p>
                </div>
                <div className={`p-3 rounded-lg border ${
                    theme === 'dark' ? 'bg-gray-700 border-gray-600' : 'bg-gray-50 border-gray-200'
                }`}>
                    <div className="flex items-center gap-2 mb-2">
                        <Users className="w-4 h-4 text-purple-600" />
                        <span className={`text-xs font-medium ${
                            theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
                        }`}>
                            Total
                        </span>
                    </div>
                    <p className="text-xl font-bold text-purple-600">
                        {calculateStats().total}
                    </p>
                </div>
            </div>

            {/* Tasks List with Different View Modes */}
            {(() => {
                const filteredTasks = getFilteredTasks();

                if (filteredTasks.length === 0) {
                    return (
                        <div className={`text-center py-12 ${
                            theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
                        }`}>
                            <Users className="w-16 h-16 mx-auto mb-4 opacity-50" />
                            <h3 className={`text-lg font-semibold mb-2 ${
                                theme === 'dark' ? 'text-white' : 'text-gray-900'
                            }`}>
                                {searchTerm || statusFilter !== 'all' || priorityFilter !== 'all' ? 'No matching tasks found' : 'No team tasks yet'}
                            </h3>
                            <p className="text-sm">
                                {searchTerm || statusFilter !== 'all' || priorityFilter !== 'all'
                                    ? 'Try adjusting your search or filters'
                                    : 'Team tasks assigned to you will appear here'
                                }
                            </p>
                        </div>
                    );
                }

                // Card View
                if (viewMode === 'card') {
                    return (
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                            {filteredTasks.map((task) => (
                                <div
                                    key={task.id}
                                    className={`p-4 rounded-lg border cursor-pointer transition-all hover:shadow-md ${
                                        theme === 'dark'
                                            ? 'bg-gray-700 border-gray-600 hover:bg-gray-600'
                                            : 'bg-gray-50 border-gray-200 hover:bg-white'
                                    }`}
                                    onClick={() => handleTaskClick(task)}
                                >
                                    <div className="flex items-start justify-between mb-3">
                                        <div className="flex-1">
                                            <div className="flex items-center gap-2 mb-2">
                                                <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getPriorityColor(task.priority)}`}>
                                                    {task.priority?.replace('_', ' ') || 'Scheduled'}
                                                </span>
                                                <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(task.status)}`}>
                                                    {getStatusIcon(task.status)}
                                                    <span className="ml-1">{task.status?.replace('_', ' ') || 'Pending'}</span>
                                                </span>
                                            </div>
                                            <h4 className={`font-semibold text-sm mb-1 ${
                                                theme === 'dark' ? 'text-white' : 'text-gray-900'
                                            }`}>
                                                {task.title}
                                            </h4>
                                            {task.description && (
                                                <p className={`text-xs mb-2 line-clamp-2 ${
                                                    theme === 'dark' ? 'text-gray-300' : 'text-gray-600'
                                                }`}>
                                                    {task.description}
                                                </p>
                                            )}
                                            <div className="flex items-center gap-3 text-xs text-gray-500">
                                                {task.deadline && (
                                                    <div className="flex items-center gap-1">
                                                        <Calendar className="w-3 h-3" />
                                                        <span>{new Date(task.deadline).toLocaleDateString('en-IN', { timeZone: 'Asia/Kolkata' })}</span>
                                                    </div>
                                                )}
                                                {task.estimated_hours && (
                                                    <div className="flex items-center gap-1">
                                                        <Clock className="w-3 h-3" />
                                                        <span>{task.estimated_hours}h</span>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                        <div className="flex items-center gap-2">
                                            {task.comments_count > 0 && (
                                                <div className="flex items-center gap-1 text-blue-600">
                                                    <MessageSquare className="w-3 h-3" />
                                                    <span className="text-xs">{task.comments_count}</span>
                                                </div>
                                            )}
                                            {task.attachments_count > 0 && (
                                                <div className="flex items-center gap-1 text-green-600">
                                                    <Paperclip className="w-3 h-3" />
                                                    <span className="text-xs">{task.attachments_count}</span>
                                                </div>
                                            )}
                                        </div>
                                    </div>

                                    {/* Quick Actions */}
                                    {(() => {
                                        const isUserAssigned = task.assigned_employee_ids?.includes(user.id) ||
                                                           task.assigned_to === user.id ||
                                                           task.assigned_to_name?.includes(user.name) ||
                                                           task.assigned_to_names?.some(name => name.includes(user.name));
                                        const canEditTaskStatus = task.created_by === user.id || ['admin', 'Admin', 'manager', 'Manager'].includes(user.role);

                                        // Check if all subtasks are completed
                                        const allSubtasksCompleted = task.subtasks && task.subtasks.length > 0 &&
                                            task.subtasks.every(st => st.status === 'completed');
                                        const hasNoSubtasks = !task.subtasks || task.subtasks.length === 0;

                                        return isUserAssigned && task.status !== 'completed' && (
                                            <div className="flex items-center gap-2 pt-3 border-t border-gray-200 dark:border-gray-600">
                                                {task.status === 'pending' && (
                                                    <button
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            handleStatusUpdate(task.id, 'in_progress');
                                                        }}
                                                        className="px-3 py-1 bg-blue-600 hover:bg-blue-700 text-white text-xs rounded transition-colors"
                                                    >
                                                        Start Task
                                                    </button>
                                                )}

                                                {/* For assignees: Show Request Review when all subtasks are completed */}
                                                {task.status === 'in_progress' && !canEditTaskStatus && allSubtasksCompleted && (
                                                    <button
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            handleStatusUpdate(task.id, 'under_review');
                                                        }}
                                                        className="px-3 py-1 bg-yellow-600 hover:bg-yellow-700 text-white text-xs rounded transition-colors"
                                                    >
                                                        Request Review
                                                    </button>
                                                )}

                                                {/* For assignees: Show working status when subtasks not completed */}
                                                {task.status === 'in_progress' && !canEditTaskStatus && !allSubtasksCompleted && (
                                                    <span className="px-3 py-1 bg-gray-100 text-gray-600 text-xs rounded">
                                                        Complete subtasks to request review
                                                    </span>
                                                )}

                                                {/* For admins/managers: Show Mark Complete when task is under review */}
                                                {task.status === 'under_review' && canEditTaskStatus && (
                                                    <button
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            handleStatusUpdate(task.id, 'completed');
                                                        }}
                                                        className="px-3 py-1 bg-green-600 hover:bg-green-700 text-white text-xs rounded transition-colors"
                                                    >
                                                        Mark Complete
                                                    </button>
                                                )}

                                                {/* For admins/managers: Show complete option for tasks without subtasks */}
                                                {task.status === 'in_progress' && canEditTaskStatus && hasNoSubtasks && (
                                                    <button
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            handleStatusUpdate(task.id, 'completed');
                                                        }}
                                                        className="px-3 py-1 bg-green-600 hover:bg-green-700 text-white text-xs rounded transition-colors"
                                                    >
                                                        Mark Complete
                                                    </button>
                                                )}

                                                                                            </div>
                                        );
                                    })()}
                                </div>
                            ))}
                        </div>
                    );
                }

                // List View
                if (viewMode === 'list') {
                    return (
                        <div className="space-y-2">
                            {filteredTasks.map((task) => {
                                const isUserAssigned = task.assigned_employee_ids?.includes(user.id) ||
                                                       task.assigned_to === user.id ||
                                                       task.assigned_to_name?.includes(user.name) ||
                                                       task.assigned_to_names?.some(name => name.includes(user.name));
                                const canEditTaskStatus = task.created_by === user.id || ['admin', 'Admin', 'manager', 'Manager'].includes(user.role);

                                // Check if all subtasks are completed
                                const allSubtasksCompleted = task.subtasks && task.subtasks.length > 0 &&
                                    task.subtasks.every(st => st.status === 'completed');
                                const hasNoSubtasks = !task.subtasks || task.subtasks.length === 0;

                                return (
                                    <div
                                        key={task.id}
                                        className={`p-4 rounded-lg border cursor-pointer transition-all hover:shadow-md ${
                                            theme === 'dark'
                                                ? 'bg-gray-700 border-gray-600 hover:bg-gray-600'
                                                : 'bg-gray-50 border-gray-200 hover:bg-white'
                                        }`}
                                        onClick={() => handleTaskClick(task)}
                                    >
                                        <div className="flex items-center justify-between">
                                            <div className="flex items-center gap-4 flex-1">
                                                <div className={`w-3 h-3 rounded-full flex items-center justify-center text-xs font-medium ${getStatusColor(task.status)}`}>
                                                    {getStatusIcon(task.status)}
                                                </div>
                                                <div className="flex-1">
                                                    <h4 className={`font-semibold text-sm ${
                                                        theme === 'dark' ? 'text-white' : 'text-gray-900'
                                                    }`}>
                                                        {task.title}
                                                    </h4>
                                                    <p className={`text-xs ${
                                                        theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
                                                    }`}>
                                                        {task.description || 'No description'}
                                                    </p>
                                                </div>
                                                <div className="flex items-center gap-2">
                                                    <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getPriorityColor(task.priority)}`}>
                                                        {task.priority?.replace('_', ' ') || 'Scheduled'}
                                                    </span>
                                                    {task.deadline && (
                                                        <span className="text-xs text-gray-500">
                                                            Due: {new Date(task.deadline).toLocaleDateString('en-IN', { timeZone: 'Asia/Kolkata' })}
                                                        </span>
                                                    )}
                                                </div>
                                            </div>
                                            <div className="flex items-center gap-2">
                                                {task.comments_count > 0 && (
                                                    <div className="flex items-center gap-1 text-blue-600">
                                                        <MessageSquare className="w-3 h-3" />
                                                        <span className="text-xs">{task.comments_count}</span>
                                                    </div>
                                                )}
                                                {task.attachments_count > 0 && (
                                                    <div className="flex items-center gap-1 text-green-600">
                                                        <Paperclip className="w-3 h-3" />
                                                        <span className="text-xs">{task.attachments_count}</span>
                                                    </div>
                                                )}
                                            </div>
                                        </div>

                                        {/* Quick Actions */}
                                        {isUserAssigned && task.status !== 'completed' && (
                                            <div className="flex items-center gap-2 mt-3 pt-3 border-t border-gray-200 dark:border-gray-600">
                                                {task.status === 'pending' && (
                                                    <button
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            handleStatusUpdate(task.id, 'in_progress');
                                                        }}
                                                        className="px-3 py-1 bg-blue-600 hover:bg-blue-700 text-white text-xs rounded transition-colors"
                                                    >
                                                        Start Task
                                                    </button>
                                                )}

                                                {/* For assignees: Show Request Review when all subtasks are completed */}
                                                {task.status === 'in_progress' && !canEditTaskStatus && allSubtasksCompleted && (
                                                    <button
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            handleStatusUpdate(task.id, 'under_review');
                                                        }}
                                                        className="px-3 py-1 bg-yellow-600 hover:bg-yellow-700 text-white text-xs rounded transition-colors"
                                                    >
                                                        Request Review
                                                    </button>
                                                )}

                                                {/* For assignees: Show working status when subtasks not completed */}
                                                {task.status === 'in_progress' && !canEditTaskStatus && !allSubtasksCompleted && (
                                                    <span className="px-3 py-1 bg-gray-100 text-gray-600 text-xs rounded">
                                                        Complete subtasks to request review
                                                    </span>
                                                )}

                                                {/* For admins/managers: Show Mark Complete when task is under review */}
                                                {task.status === 'under_review' && canEditTaskStatus && (
                                                    <button
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            handleStatusUpdate(task.id, 'completed');
                                                        }}
                                                        className="px-3 py-1 bg-green-600 hover:bg-green-700 text-white text-xs rounded transition-colors"
                                                    >
                                                        Mark Complete
                                                    </button>
                                                )}

                                                {/* For admins/managers: Show complete option for tasks without subtasks */}
                                                {task.status === 'in_progress' && canEditTaskStatus && hasNoSubtasks && (
                                                    <button
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            handleStatusUpdate(task.id, 'completed');
                                                        }}
                                                        className="px-3 py-1 bg-green-600 hover:bg-green-700 text-white text-xs rounded transition-colors"
                                                    >
                                                        Mark Complete
                                                    </button>
                                                )}

                                                                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    );
                }

                // Kanban View
                if (viewMode === 'kanban') {
                    const statusColumns = [
                        { id: 'pending', title: 'Pending', color: 'border-gray-400' },
                        { id: 'in_progress', title: 'In Progress', color: 'border-blue-400' },
                        { id: 'under_review', title: 'Under Review', color: 'border-yellow-400' },
                        { id: 'completed', title: 'Completed', color: 'border-green-400' },
                        { id: 'on_hold', title: 'On Hold', color: 'border-orange-400' }
                    ];

                    return (
                        <div className="flex gap-4 overflow-x-auto pb-4">
                            {statusColumns.map(column => (
                                <div key={column.id} className="flex-1 min-w-72">
                                    <div className={`p-3 rounded-t-lg border ${column.color} ${
                                        theme === 'dark' ? 'bg-gray-700' : 'bg-white'
                                    }`}>
                                        <h5 className={`font-semibold text-sm ${
                                            theme === 'dark' ? 'text-white' : 'text-gray-900'
                                        }`}>
                                            {column.title}
                                        </h5>
                                        <span className={`text-xs ${
                                            theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
                                        }`}>
                                            {filteredTasks.filter(task => task.status === column.id).length} tasks
                                        </span>
                                    </div>
                                    <div className={`p-2 space-y-2 min-h-[200px] ${column.color} ${
                                        theme === 'dark' ? 'bg-gray-700' : 'bg-white'
                                    } rounded-b-lg`}>
                                        {filteredTasks
                                            .filter(task => task.status === column.id)
                                            .map(task => (
                                                <div
                                                    key={task.id}
                                                    className={`p-3 rounded-lg border-2 cursor-pointer transition-all ${
                                                        theme === 'dark' ? 'bg-gray-800 border-gray-600 hover:border-gray-500' : 'bg-white border-gray-200 hover:border-gray-300'
                                                    }`}
                                                    onClick={() => handleTaskClick(task)}
                                                >
                                                    <div className="flex items-center justify-between mb-2">
                                                        <h6 className={`font-medium text-xs ${
                                                            theme === 'dark' ? 'text-white' : 'text-gray-900'
                                                        }`}>
                                                            {task.title}
                                                        </h6>
                                                        <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getPriorityColor(task.priority)}`}>
                                                            {task.priority?.replace('_', ' ')}
                                                        </span>
                                                    </div>
                                                    {task.deadline && (
                                                        <p className={`text-xs ${
                                                            theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
                                                        }`}>
                                                            Due: {new Date(task.deadline).toLocaleDateString('en-IN', { timeZone: 'Asia/Kolkata' })}
                                                        </p>
                                                    )}
                                                    <div className="flex items-center gap-2 mt-2">
                                                        {task.comments_count > 0 && (
                                                            <span className="text-blue-600 text-xs">
                                                                <MessageSquare className="w-3 h-3" /> {task.comments_count}
                                                            </span>
                                                        )}
                                                        {task.attachments_count > 0 && (
                                                            <span className="text-green-600 text-xs">
                                                                <Paperclip className="w-3 h-3" /> {task.attachments_count}
                                                            </span>
                                                        )}
                                                    </div>
                                                </div>
                                            ))}
                                    </div>
                                </div>
                            ))}
                        </div>
                    );
                }

                // Calendar View
                if (viewMode === 'calendar') {

                    const getDaysInMonth = (date) => {
                        const year = date.getFullYear();
                        const month = date.getMonth();
                        const firstDay = new Date(year, month, 1);
                        const lastDay = new Date(year, month + 1, 0);
                        const daysInMonth = lastDay.getDate();
                        const startingDayOfWeek = firstDay.getDay();

                        const days = [];

                        // Add empty cells for days before month starts
                        for (let i = 0; i < startingDayOfWeek; i++) {
                            days.push(null);
                        }

                        // Add days of the month
                        for (let i = 1; i <= daysInMonth; i++) {
                            days.push(i);
                        }

                        return days;
                    };

                    const getTasksForDate = (day) => {
                        if (!day) return [];

                        const dateStr = new Date(
                            currentMonth.getFullYear(),
                            currentMonth.getMonth(),
                            day
                        ).toDateString();

                        return filteredTasks.filter(task => {
                            if (!task.deadline) return false;
                            return new Date(task.deadline).toDateString() === dateStr;
                        });
                    };

                    const navigateMonth = (direction) => {
                        setCurrentMonth(prev => {
                            const newDate = new Date(prev);
                            if (direction === 'prev') {
                                newDate.setMonth(newDate.getMonth() - 1);
                            } else {
                                newDate.setMonth(newDate.getMonth() + 1);
                            }
                            return newDate;
                        });
                    };

                    const weekDays = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
                    const days = getDaysInMonth(currentMonth);

                    return (
                        <div className={`p-6 rounded-lg border ${
                            theme === 'dark' ? 'bg-gray-700 border-gray-600' : 'bg-gray-50 border-gray-200'
                        }`}>
                            {/* Calendar Header */}
                            <div className="flex items-center justify-between mb-6">
                                <div className="flex items-center gap-4">
                                    <button
                                        onClick={() => navigateMonth('prev')}
                                        className={`p-2 rounded-lg transition-colors ${
                                            theme === 'dark'
                                                ? 'hover:bg-gray-600 text-gray-400'
                                                : 'hover:bg-gray-200 text-gray-600'
                                        }`}
                                    >
                                        <RefreshCw className="w-4 h-4 rotate-180" />
                                    </button>
                                    <h4 className={`font-semibold text-lg ${
                                        theme === 'dark' ? 'text-white' : 'text-gray-900'
                                    }`}>
                                        {currentMonth.toLocaleDateString('en-US', { month: 'long', year: 'numeric', timeZone: 'Asia/Kolkata' })}
                                    </h4>
                                    <button
                                        onClick={() => navigateMonth('next')}
                                        className={`p-2 rounded-lg transition-colors ${
                                            theme === 'dark'
                                                ? 'hover:bg-gray-600 text-gray-400'
                                                : 'hover:bg-gray-200 text-gray-600'
                                        }`}
                                    >
                                        <RefreshCw className="w-4 h-4" />
                                    </button>
                                </div>
                                <button
                                    onClick={() => setCurrentMonth(new Date())}
                                    className={`px-3 py-1 text-sm rounded-lg border ${
                                        theme === 'dark'
                                            ? 'border-gray-600 text-gray-400 hover:bg-gray-600'
                                            : 'border-gray-300 text-gray-600 hover:bg-gray-100'
                                    }`}
                                >
                                    Today
                                </button>
                            </div>

                            {/* Calendar Grid */}
                            <div className="grid grid-cols-7 gap-1">
                                {/* Week day headers */}
                                {weekDays.map(day => (
                                    <div key={day} className={`text-center py-2 text-xs font-medium ${
                                        theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
                                    }`}>
                                        {day}
                                    </div>
                                ))}

                                {/* Calendar days */}
                                {days.map((day, index) => {
                                    const tasksForDay = getTasksForDate(day);
                                    const isToday = day && new Date().toDateString() ===
                                        new Date(currentMonth.getFullYear(), currentMonth.getMonth(), day).toDateString();

                                    return (
                                        <div
                                            key={index}
                                            className={`min-h-[80px] p-2 border rounded ${
                                                !day
                                                    ? theme === 'dark' ? 'bg-gray-800 border-gray-700' : 'bg-gray-100 border-gray-200'
                                                    : isToday
                                                        ? theme === 'dark'
                                                            ? 'bg-blue-900/30 border-blue-600'
                                                            : 'bg-blue-50 border-blue-300'
                                                        : theme === 'dark'
                                                            ? 'bg-gray-800 border-gray-700 hover:bg-gray-700'
                                                            : 'bg-white border-gray-200 hover:bg-gray-50'
                                            }`}
                                        >
                                            {day && (
                                                <>
                                                    <div className={`text-sm font-medium mb-1 ${
                                                        isToday
                                                            ? 'text-blue-600'
                                                            : theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                                                    }`}>
                                                        {day}
                                                    </div>
                                                    <div className="space-y-1">
                                                        {tasksForDay.slice(0, 3).map(task => (
                                                            <div
                                                                key={task.id}
                                                                onClick={() => handleTaskClick(task)}
                                                                className={`text-xs p-1 rounded cursor-pointer truncate ${
                                                                    task.status === 'completed'
                                                                        ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
                                                                        : task.status === 'in_progress'
                                                                            ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400'
                                                                            : task.priority === 'immediate' || task.priority === 'urgent'
                                                                                ? 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400'
                                                                                : theme === 'dark'
                                                                                    ? 'bg-gray-700 text-gray-300'
                                                                                    : 'bg-gray-100 text-gray-700'
                                                                }`}
                                                                title={task.title}
                                                            >
                                                                {task.title}
                                                            </div>
                                                        ))}
                                                        {tasksForDay.length > 3 && (
                                                            <div className={`text-xs ${
                                                                theme === 'dark' ? 'text-gray-500' : 'text-gray-400'
                                                            }`}>
                                                                +{tasksForDay.length - 3} more
                                                            </div>
                                                        )}
                                                    </div>
                                                </>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>

                            {/* Task Summary */}
                            <div className={`mt-6 p-4 rounded-lg ${
                                theme === 'dark' ? 'bg-gray-800' : 'bg-white'
                            }`}>
                                <h5 className={`font-medium mb-3 ${
                                    theme === 'dark' ? 'text-white' : 'text-gray-900'
                                }`}>
                                    Tasks This Month
                                </h5>
                                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                                    <div className="text-center">
                                        <div className="text-2xl font-bold text-blue-600">
                                            {filteredTasks.filter(task => task.deadline &&
                                                new Date(task.deadline).getMonth() === currentMonth.getMonth() &&
                                                new Date(task.deadline).getFullYear() === currentMonth.getFullYear()
                                            ).length}
                                        </div>
                                        <div className="text-xs text-gray-500">Total Tasks</div>
                                    </div>
                                    <div className="text-center">
                                        <div className="text-2xl font-bold text-green-600">
                                            {filteredTasks.filter(task =>
                                                task.status === 'completed' && task.deadline &&
                                                new Date(task.deadline).getMonth() === currentMonth.getMonth() &&
                                                new Date(task.deadline).getFullYear() === currentMonth.getFullYear()
                                            ).length}
                                        </div>
                                        <div className="text-xs text-gray-500">Completed</div>
                                    </div>
                                    <div className="text-center">
                                        <div className="text-2xl font-bold text-orange-600">
                                            {filteredTasks.filter(task =>
                                                task.status !== 'completed' && task.deadline &&
                                                new Date(task.deadline) < new Date() &&
                                                new Date(task.deadline).getMonth() === currentMonth.getMonth() &&
                                                new Date(task.deadline).getFullYear() === currentMonth.getFullYear()
                                            ).length}
                                        </div>
                                        <div className="text-xs text-gray-500">Overdue</div>
                                    </div>
                                    <div className="text-center">
                                        <div className="text-2xl font-bold text-purple-600">
                                            {filteredTasks.filter(task =>
                                                task.deadline &&
                                                new Date(task.deadline) >= new Date() &&
                                                new Date(task.deadline).getMonth() === currentMonth.getMonth() &&
                                                new Date(task.deadline).getFullYear() === currentMonth.getFullYear()
                                            ).length}
                                        </div>
                                        <div className="text-xs text-gray-500">Upcoming</div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    );
                }
            })()}

            {/* Task Detail Modal with Full Functionality */}
            {showDetailModal && selectedTask && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                    <div className={`rounded-lg shadow-xl max-w-5xl w-full max-h-[90vh] overflow-hidden flex flex-col ${
                        theme === 'dark' ? 'bg-gray-800' : 'bg-white'
                    }`}>
                        {/* Header */}
                        <div className={`p-6 border-b ${
                            theme === 'dark' ? 'border-gray-700' : 'border-gray-200'
                        }`}>
                            <div className="flex items-center justify-between">
                                <div className="flex-1">
                                    <h2 className={`text-2xl font-bold ${
                                        theme === 'dark' ? 'text-white' : 'text-gray-900'
                                    }`}>
                                        {selectedTask.title}
                                    </h2>
                                    <p className={`text-sm ${
                                        theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
                                    }`}>
                                        Created by {selectedTask.created_by_name || 'You'} • {new Date(selectedTask.created_at).toLocaleDateString('en-IN', { timeZone: 'Asia/Kolkata' })}
                                    </p>
                                </div>
                                <button
                                    onClick={() => {
                                        setShowDetailModal(false);
                                        setTaskDetails(null);
                                        setNewComment('');
                                        setSelectedFile(null);
                                        setSubtaskComments({});
                                        setAttachmentDescription('');
                                    }}
                                    className={`p-2 rounded-lg transition-colors ${
                                        theme === 'dark'
                                            ? 'hover:bg-gray-700 text-gray-400'
                                            : 'hover:bg-gray-100 text-gray-600'
                                    }`}
                                >
                                    <X className="w-6 h-6" />
                                </button>
                            </div>
                        </div>

                        {/* Tabs */}
                        <div className="flex border-b border-gray-200 dark:border-gray-700 overflow-x-auto">
                            {tabs.map(tab => {
                                const Icon = tab.icon;
                                return (
                                    <button
                                        key={tab.id}
                                        onClick={() => setActiveTab(tab.id)}
                                        className={`flex items-center gap-2 px-6 py-3 border-b-2 transition-colors whitespace-nowrap ${
                                            activeTab === tab.id
                                                ? 'border-blue-600 text-blue-600 dark:text-blue-400'
                                                : 'border-transparent text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'
                                        }`}
                                    >
                                        <Icon className="w-4 h-4" />
                                        <span>{tab.label}</span>
                                        {tab.count > 0 && (
                                            <span className="px-2 py-0.5 text-xs bg-gray-200 dark:bg-gray-700 rounded-full">
                                                {tab.count}
                                            </span>
                                        )}
                                    </button>
                                );
                            })}
                        </div>

                        {/* Tab Content */}
                        <div className="flex-1 overflow-y-auto p-6">
                            {loadingDetails ? (
                                <div className="flex items-center justify-center py-8">
                                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                                </div>
                            ) : (
                                <>
                                    {/* Overview Tab */}
                                    {activeTab === 'overview' && (
                                        <div className="space-y-6">
                                            <div>
                                                <h3 className={`text-sm font-medium ${
                                                    theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                                                }`}>
                                                    Description
                                                </h3>
                                                <p className={`mt-2 ${
                                                    theme === 'dark' ? 'text-gray-300' : 'text-gray-600'
                                                }`}>
                                                    {selectedTask.description || 'No description'}
                                                </p>
                                            </div>

                                            <div className="grid grid-cols-2 gap-4">
                                                <div>
                                                    <span className={`font-medium text-sm ${
                                                        theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                                                    }`}>
                                                        Status:
                                                    </span>
                                                    <span className={`ml-2 px-2 py-1 rounded-full text-xs ${getStatusColor(selectedTask.status)}`}>
                                                        {selectedTask.status?.replace('_', ' ') || 'Pending'}
                                                    </span>
                                                </div>
                                                <div>
                                                    <span className={`font-medium text-sm ${
                                                        theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                                                    }`}>
                                                        Priority:
                                                    </span>
                                                    <span className={`ml-2 px-2 py-1 rounded-full text-xs ${getPriorityColor(selectedTask.priority)}`}>
                                                        {selectedTask.priority?.replace('_', ' ') || 'Scheduled'}
                                                    </span>
                                                </div>
                                                {selectedTask.deadline && (
                                                    <div>
                                                        <span className={`font-medium text-sm ${
                                                            theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                                                        }`}>
                                                            Deadline:
                                                        </span>
                                                        <span className={`ml-2 ${
                                                            theme === 'dark' ? 'text-gray-300' : 'text-gray-600'
                                                        }`}>
                                                            {new Date(selectedTask.deadline).toLocaleDateString('en-IN', { timeZone: 'Asia/Kolkata' })}
                                                        </span>
                                                    </div>
                                                )}
                                                {selectedTask.estimated_hours && (
                                                    <div>
                                                        <span className={`font-medium text-sm ${
                                                            theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                                                        }`}>
                                                            Estimated Hours:
                                                        </span>
                                                        <span className={`ml-2 ${
                                                            theme === 'dark' ? 'text-gray-300' : 'text-gray-600'
                                                        }`}>
                                                            {selectedTask.estimated_hours}h
                                                        </span>
                                                    </div>
                                                )}
                                            </div>

                                            {/* Assignees */}
                                            {(() => {
                                                const assignedNames = getAssignedEmployeeNames(selectedTask);
                                                return assignedNames.length > 0 && (
                                                    <div>
                                                        <h3 className={`text-sm font-medium mb-3 ${
                                                            theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                                                        }`}>
                                                            Assignees
                                                        </h3>
                                                        <div className="flex flex-wrap gap-2">
                                                            {assignedNames.map((name, index) => (
                                                                <span
                                                                    key={index}
                                                                    className={`px-3 py-1 rounded-full text-sm ${
                                                                        theme === 'dark' ? 'bg-gray-700 text-gray-300' : 'bg-gray-100 text-gray-700'
                                                                    }`}
                                                                >
                                                                    {name}
                                                                </span>
                                                            ))}
                                                        </div>
                                                    </div>
                                                );
                                            })()}
                                        </div>
                                    )}

                                    {/* Sub-tasks Tab */}
                                    {activeTab === 'subtasks' && taskDetails?.subtasks && (
                                        <div className="space-y-4">
                                            {taskDetails.subtasks.map(subtask => {
                                                const statusColors = {
                                                    pending: 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300',
                                                    in_progress: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
                                                    on_hold: 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400',
                                                    completed: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
                                                };

                                                return (
                                                    <div key={subtask.id} className={`p-4 rounded-lg ${
                                                        theme === 'dark' ? 'bg-gray-700' : 'bg-gray-50'
                                                    }`}>
                                                        <div className="flex items-start justify-between gap-2 mb-2">
                                                            <h4 className={`font-medium flex-1 ${
                                                                subtask.status === 'completed' ? 'line-through text-gray-500' :
                                                                theme === 'dark' ? 'text-white' : 'text-gray-900'
                                                            }`}>
                                                                {subtask.title}
                                                            </h4>
                                                            <span className={`px-2 py-1 rounded-full text-xs font-medium ${statusColors[subtask.status] || statusColors.pending}`}>
                                                                {subtask.status.replace('_', ' ').toUpperCase()}
                                                            </span>
                                                        </div>
                                                        {subtask.description && (
                                                            <p className={`text-sm mb-3 ${
                                                                subtask.status === 'completed' ? 'line-through text-gray-400' :
                                                                theme === 'dark' ? 'text-gray-300' : 'text-gray-600'
                                                            }`}>
                                                                {subtask.description}
                                                            </p>
                                                        )}
                                                        {canChangeSubtaskStatus(selectedTask, subtask) ? (
                                                            <select
                                                                value={subtask.status}
                                                                onChange={(e) => handleUpdateSubtaskStatus(subtask.id, e.target.value)}
                                                                className={`px-3 py-1 text-sm border rounded ${
                                                                    theme === 'dark'
                                                                        ? 'bg-gray-600 border-gray-500 text-white'
                                                                        : 'bg-white border-gray-300'
                                                                }`}
                                                            >
                                                                <option value="pending">Pending</option>
                                                                <option value="in_progress">In Progress</option>
                                                                <option value="on_hold">On Hold</option>
                                                                <option value="completed">Completed</option>
                                                            </select>
                                                        ) : (
                                                            <span className={`px-3 py-1 text-sm border rounded ${
                                                                theme === 'dark'
                                                                    ? 'bg-gray-600 border-gray-500 text-gray-400'
                                                                    : 'bg-gray-100 border-gray-200 text-gray-600'
                                                            }`}>
                                                                {subtask.status.replace('_', ' ').toUpperCase()}
                                                            </span>
                                                        )}

                                                        {/* Comment Section */}
                                                        {canInteractWithTask(selectedTask) && (
                                                            <div className="mt-3 pt-3 border-t border-gray-200 dark:border-gray-600">
                                                                <div className="flex gap-2">
                                                                    <input
                                                                        type="text"
                                                                        value={subtaskComments[subtask.id] || ''}
                                                                        onChange={(e) => setSubtaskComments({ ...subtaskComments, [subtask.id]: e.target.value })}
                                                                        placeholder="Add a comment (optional)..."
                                                                        className={`flex-1 px-3 py-1 text-sm border rounded ${
                                                                            theme === 'dark'
                                                                                ? 'bg-gray-600 border-gray-500 text-white'
                                                                                : 'bg-white border-gray-300'
                                                                        }`}
                                                                    />
                                                                    <button
                                                                        onClick={() => handleAddSubtaskComment(subtask.id)}
                                                                        disabled={!subtaskComments[subtask.id]?.trim()}
                                                                        className={`px-3 py-1 text-sm rounded disabled:opacity-50 disabled:cursor-not-allowed ${
                                                                            theme === 'dark'
                                                                                ? 'bg-blue-600 text-white hover:bg-blue-700'
                                                                                : 'bg-blue-600 text-white hover:bg-blue-700'
                                                                        }`}
                                                                    >
                                                                        <MessageSquare className="w-4 h-4" />
                                                                    </button>
                                                                </div>
                                                            </div>
                                                        )}
                                                    </div>
                                                );
                                            })}
                                            {(!taskDetails.subtasks || taskDetails.subtasks.length === 0) && (
                                                <div className={`text-center py-8 ${
                                                    theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
                                                }`}>
                                                    <CheckSquare className="w-12 h-12 mx-auto mb-3 opacity-50" />
                                                    <p>No subtasks yet</p>
                                                </div>
                                            )}
                                        </div>
                                    )}

                                    {/* Comments Tab */}
                                    {activeTab === 'comments' && (
                                        <div className="space-y-4">
                                            {taskDetails?.comments?.map(comment => {
                                                // Find subtask if this is a subtask comment
                                                const subtask = comment.subtask_id
                                                    ? taskDetails.subtasks?.find(st => st.id === comment.subtask_id)
                                                    : null;

                                                return (
                                                    <div key={comment.id} className="flex gap-3">
                                                        <div className="flex-1">
                                                            <div className="flex items-center gap-2 mb-1">
                                                                <span className={`font-medium ${
                                                                    theme === 'dark' ? 'text-white' : 'text-gray-900'
                                                                }`}>
                                                                    {comment.employee_name}
                                                                </span>
                                                                <span className="text-xs text-gray-500">
                                                                    {new Date(comment.created_at).toLocaleString('en-IN', { timeZone: 'Asia/Kolkata' })}
                                                                </span>
                                                                {subtask && (
                                                                    <span className="px-2 py-0.5 bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400 text-xs rounded-full">
                                                                        Subtask: {subtask.title}
                                                                    </span>
                                                                )}
                                                            </div>
                                                            <p className={theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}>
                                                                {comment.comment}
                                                            </p>
                                                        </div>
                                                    </div>
                                                );
                                            })}

                                            {/* Add Comment */}
                                            {canInteractWithTask(selectedTask) && (
                                                <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
                                                    <textarea
                                                        value={newComment}
                                                        onChange={(e) => setNewComment(e.target.value)}
                                                        placeholder="Add a comment..."
                                                        rows={3}
                                                        className={`w-full px-4 py-2 border rounded ${
                                                            theme === 'dark'
                                                                ? 'bg-gray-700 border-gray-600 text-white'
                                                                : 'bg-white border-gray-300'
                                                        }`}
                                                    />
                                                    <button
                                                        onClick={handleAddComment}
                                                        disabled={!newComment.trim()}
                                                        className="mt-2 px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                                                    >
                                                        Add Comment
                                                    </button>
                                                </div>
                                            )}

                                            {(!taskDetails?.comments || taskDetails.comments.length === 0) && (
                                                <div className={`text-center py-8 ${
                                                    theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
                                                }`}>
                                                    <MessageSquare className="w-12 h-12 mx-auto mb-3 opacity-50" />
                                                    <p>No comments yet</p>
                                                </div>
                                            )}
                                        </div>
                                    )}

                                    {/* Attachments Tab */}
                                    {activeTab === 'attachments' && (
                                        <div className="space-y-4">
                                            {taskDetails?.attachments?.map(attachment => (
                                                <div key={attachment.id} className={`p-4 rounded-lg ${
                                                    theme === 'dark' ? 'bg-gray-700' : 'bg-gray-50'
                                                }`}>
                                                    <div className="flex items-start gap-3">
                                                        <Paperclip className="w-5 h-5 text-gray-600 flex-shrink-0 mt-1" />
                                                        <div className="flex-1">
                                                            <a href={attachment.file_url} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline font-medium">
                                                                {attachment.file_name}
                                                            </a>
                                                            <p className="text-xs text-gray-500 mt-1">
                                                                Uploaded by {attachment.uploaded_by_name} • {new Date(attachment.uploaded_at).toLocaleDateString('en-IN', { timeZone: 'Asia/Kolkata' })}
                                                            </p>
                                                            {attachment.description && (
                                                                <p className={`text-sm mt-2 p-2 rounded ${
                                                                    theme === 'dark'
                                                                        ? 'bg-gray-600 text-gray-300'
                                                                        : 'bg-gray-100 text-gray-700'
                                                                }`}>
                                                                    {attachment.description}
                                                                </p>
                                                            )}
                                                        </div>
                                                    </div>
                                                </div>
                                            ))}

                                            {/* Upload File */}
                                            {canInteractWithTask(selectedTask) && (
                                                <div className={`p-4 rounded-lg ${
                                                    theme === 'dark' ? 'bg-gray-700' : 'bg-gray-50'
                                                }`}>
                                                    <div>
                                                        <label className={`block text-sm font-medium mb-2 ${
                                                            theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                                                        }`}>
                                                            Select File
                                                        </label>
                                                        <input
                                                            type="file"
                                                            onChange={(e) => setSelectedFile(e.target.files[0])}
                                                            className={`w-full px-3 py-2 border rounded ${
                                                                theme === 'dark'
                                                                    ? 'bg-gray-600 border-gray-500 text-white'
                                                                    : 'bg-white border-gray-300'
                                                            }`}
                                                        />
                                                    </div>
                                                    {selectedFile && (
                                                        <>
                                                            <div className="mt-3">
                                                                <label className={`block text-sm font-medium mb-2 ${
                                                                    theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                                                                }`}>
                                                                    Description (Optional)
                                                                </label>
                                                                <textarea
                                                                    value={attachmentDescription}
                                                                    onChange={(e) => setAttachmentDescription(e.target.value)}
                                                                    placeholder="Add a description or comment about this file..."
                                                                    rows={2}
                                                                    className={`w-full px-3 py-2 border rounded ${
                                                                        theme === 'dark'
                                                                            ? 'bg-gray-600 border-gray-500 text-white'
                                                                            : 'bg-white border-gray-300'
                                                                    }`}
                                                                />
                                                            </div>
                                                            <button
                                                                onClick={handleFileUpload}
                                                                className="mt-3 w-full px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 flex items-center justify-center gap-2"
                                                            >
                                                                <Paperclip className="w-4 h-4" />
                                                                Upload Attachment
                                                            </button>
                                                        </>
                                                    )}
                                                </div>
                                            )}

                                            {(!taskDetails?.attachments || taskDetails.attachments.length === 0) && (
                                                <div className={`text-center py-8 ${
                                                    theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
                                                }`}>
                                                    <Paperclip className="w-12 h-12 mx-auto mb-3 opacity-50" />
                                                    <p>No attachments yet</p>
                                                </div>
                                            )}
                                        </div>
                                    )}

                                    {/* Activity Tab */}
                                    {activeTab === 'activity' && (
                                        <div className="space-y-4">
                                            {taskDetails?.activity_log?.map(activity => (
                                                <div key={activity.id} className="flex gap-3">
                                                    <div className="flex-shrink-0 w-2 h-2 mt-2 bg-blue-600 rounded-full"></div>
                                                    <div className="flex-1">
                                                        <p className={`text-sm ${
                                                            theme === 'dark' ? 'text-white' : 'text-gray-900'
                                                        }`}>
                                                            <span className="font-medium">{activity.employee_name}</span> {activity.activity_description}
                                                        </p>
                                                        <p className="text-xs text-gray-500">
                                                            {new Date(activity.created_at).toLocaleString('en-IN', { timeZone: 'Asia/Kolkata' })}
                                                        </p>
                                                    </div>
                                                </div>
                                            ))}

                                            {(!taskDetails?.activity_log || taskDetails.activity_log.length === 0) && (
                                                <div className={`text-center py-8 ${
                                                    theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
                                                }`}>
                                                    <Activity className="w-12 h-12 mx-auto mb-3 opacity-50" />
                                                    <p>No activity yet</p>
                                                </div>
                                            )}
                                        </div>
                                    )}

                                    {/* Watchers Tab */}
                                    {activeTab === 'watchers' && (
                                        <div className="space-y-4">
                                            {taskDetails?.watchers?.map(watcher => (
                                                <div key={watcher.id} className={`flex items-center justify-between p-4 rounded-lg ${
                                                    theme === 'dark' ? 'bg-gray-700' : 'bg-gray-50'
                                                }`}>
                                                    <div className="flex items-center gap-3">
                                                        <div>
                                                            <p className={`font-medium ${
                                                                theme === 'dark' ? 'text-white' : 'text-gray-900'
                                                            }`}>
                                                                {watcher.employee_name}
                                                            </p>
                                                            <p className={`text-sm ${
                                                                theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
                                                            }`}>
                                                                {watcher.employee_email}
                                                            </p>
                                                        </div>
                                                    </div>
                                                </div>
                                            ))}

                                            {(!taskDetails?.watchers || taskDetails.watchers.length === 0) && (
                                                <div className={`text-center py-8 ${
                                                    theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
                                                }`}>
                                                    <Eye className="w-12 h-12 mx-auto mb-3 opacity-50" />
                                                    <p>No watchers yet</p>
                                                </div>
                                            )}
                                        </div>
                                    )}
                                </>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default TeamTasksWidget;